import { type Opportunity } from "@entities";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { getRpcErrorMessage } from "@shared/frontend/functions/get-rpc-error-message.function";
import {
  OPPORTUNITY_TITLE_MAX_LENGTH,
  VOLUNTEER_DESCRIPTION_MAX_LENGTH,
} from "@shared/helpers/constants";
import {
  MATCH_WEIGHT_MAX,
  MATCH_WEIGHT_MIN,
} from "@shared/helpers/match-weight";
import { ADMIN_GROUP_DEFAULT_AMOUNT, ADMIN_GROUP_LOCATION_PER_INSTANCE } from "@shared/helpers/opportunity-group-admin-copy";
import { Roles } from "@shared/Roles";
import { linko } from "@shared/linko";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FC } from "react";
import { remult } from "remult";
import { useRequest } from "../../contexts/RequestProvider";
import { useOrganization } from "../../contexts/OrganizationProvider";
import { useForm } from "../../helpers/forms/FormProvider";
import { ManagerSelect } from "../add-request/ManagersSelect";
import { FormGoogleAutocomplete } from "../form-components/form-google-autocomplete/FormGoogleAutocomplete";
import { FormTextField } from "../form-components/form-text-field/FormTextField";
import MapPinDialog from "../mission-map/MapPinDialog";
import { AidRequestAiFormatDialog, buildFormatPayload, validatePayload, type Suggestion } from "../aid-request-ai-format/AidRequestAiFormatDialog";
import { CommunicationRecordManager } from "../CommunicationRecordManager/CommunicationRecordManager";

const DEFAULT_LOCATION_LABEL = "מיקום שנבחר במפה";

interface InfoForUserProps { }

export const InfoForUser: FC<InfoForUserProps> = ({ }) => {
  const { setCurrentEvent: setRequestDetails, currentEvent: requestDetails } =
    useRequest();
  const [isAddDestination, setIsAddDestination] = useState<boolean>(false);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [isAiFormatOpen, setIsAiFormatOpen] = useState(false);
  const [formatJobId, setFormatJobId] = useState<string | null>(null);
  const [formatError, setFormatError] = useState<string | null>(null);
  const [aiSuggestion, setAiSuggestion] = useState<Suggestion | null>(null);
  const formatGenerationRef = useRef(0);
  const [mapTarget, setMapTarget] = useState<"location" | "destination">(
    "location",
  );
  const { values, setValues } = useForm<Opportunity>();
  const { currOrganization } = useOrganization();
  const descriptionLength = String(values?.description ?? "").trim().length;
  const canFormatWithAi = descriptionLength >= 5;
  const aiFormatDisabledReason =
    descriptionLength < 5
      ? "יש להזין תיאור של לפחות 5 תווים לפני פרמוט AI"
      : "";

  const startFormatMutation = useMutation({
    mutationFn: async () => {
      const payload = buildFormatPayload(
        currOrganization?.id ?? "",
        values,
        requestDetails,
      );
      const validation = validatePayload(payload);
      if (validation) throw new Error(validation);
      if (!currOrganization?.id) throw new Error("ארגון לא נטען");
      const { data } =
        await linko.opportunity.queries.startAidRequestFormat(payload);
      return data;
    },
  });

  const formatStatusQuery = useQuery({
    queryKey: ["aid-request-ai-format", formatJobId],
    enabled: !!formatJobId,
    retry: false,
    queryFn: async () => {
      const { data } = await linko.opportunity.queries.getAidRequestFormat({
        jobId: formatJobId!,
      });
      return data;
    },
    refetchInterval: (query) => {
      if (query.state.status === "error") return false;
      const status = query.state.data?.status;
      if (!status || status === "PENDING") return 1_500;
      return false;
    },
  });

  useEffect(() => {
    if (!formatJobId || !formatStatusQuery.isError) return;
    setFormatError(
      getRpcErrorMessage(formatStatusQuery.error, "שגיאה בפרמוט AI"),
    );
    setFormatJobId(null);
  }, [formatJobId, formatStatusQuery.isError, formatStatusQuery.error]);

  useEffect(() => {
    const data = formatStatusQuery.data;
    if (!formatJobId || !data || data.jobId !== formatJobId) return;
    if (data.status === "SUCCEEDED" && data.result) {
      setAiSuggestion(data.result);
      setFormatJobId(null);
      setIsAiFormatOpen(true);
      return;
    }
    if (data.status === "FAILED") {
      setFormatError(data.errorMessage || "שגיאה בפרמוט AI");
      setFormatJobId(null);
    }
  }, [formatStatusQuery.data, formatJobId]);

  const isFormatting = startFormatMutation.isPending || !!formatJobId;

  function startFormat() {
    const generation = ++formatGenerationRef.current;
    setFormatError(null);
    setAiSuggestion(null);
    setIsAiFormatOpen(false);
    setFormatJobId(null);
    startFormatMutation.mutate(undefined, {
      onSuccess: (data) => {
        if (formatGenerationRef.current !== generation) return;
        setFormatJobId(data.jobId);
      },
      onError: (error) => {
        if (formatGenerationRef.current !== generation) return;
        setFormatError(getRpcErrorMessage(error, "שגיאה בפרמוט AI"));
      },
    });
  }

  function removeIsraelFromLocation(description: string): string {
    return description
      .replace(/,\s*ישראל\s*$/i, "")
      .replace(/\s*ישראל\s*$/i, "")
      .trim();
  }

  async function reverseGeocode(
    coordinates: google.maps.LatLngLiteral,
  ): Promise<string | null> {
    if (typeof google === "undefined" || !google.maps?.Geocoder) {
      return null;
    }
    try {
      const geocoder = new google.maps.Geocoder();
      const { results } = await geocoder.geocode({ location: coordinates });
      const address = results?.[0]?.formatted_address;
      return address ? removeIsraelFromLocation(address) : null;
    } catch (error) {
      console.error("Failed to reverse geocode:", error);
      return null;
    }
  }

  function openMapPicker(target: "location" | "destination") {
    setMapTarget(target);
    setIsMapOpen(true);
  }

  async function handleMapSave(coordinates: google.maps.LatLngLiteral) {
    // מעדכן רק את הקואורדינטות, לא משנה את שם הכתובת
    if (mapTarget === "location") {
      setRequestDetails((prev) => ({
        ...prev,
        coordinates,
      }));
      setValues((values) => {
        values.coordinates = coordinates;
      });
    } else {
      setRequestDetails((prev) => ({
        ...prev,
        destinationCoordinates: coordinates,
      }));
      setValues((values) => {
        values.destinationCoordinates = coordinates;
      });
    }

    setIsMapOpen(false);
  }

  const currentMapCoordinates =
    mapTarget === "location"
      ? requestDetails.coordinates
      : requestDetails.destinationCoordinates;

  return (
    <Stack gap="1vh">
      <Stack direction="row" justifyContent="flex-end">
        <Tooltip title={aiFormatDisabledReason || ""}>
          <span>
            <Button
              type="button"
              variant="outlined"
              size="small"
              startIcon={
                isFormatting ? (
                  <CircularProgress size={16} />
                ) : (
                  <AutoAwesomeIcon />
                )
              }
              disabled={!canFormatWithAi || isFormatting}
              onClick={startFormat}
              sx={{ borderRadius: "1rem" }}
            >
              {isFormatting ? "מפרמט..." : "פרמוט AI"}
            </Button>
          </span>
        </Tooltip>
      </Stack>
      {formatError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={startFormat}>
              ניסיון חוזר
            </Button>
          }
        >
          {formatError}
        </Alert>
      )}
      <FormTextField
        label="כותרת המשימה למתנדבים"
        withInfo="כותרת שתופיע בראש המשימה"
        name="title"
        limit={OPPORTUNITY_TITLE_MAX_LENGTH}
        showErrorText={false}
        InputProps={{
          sx: { borderRadius: "1.5rem" },
        }}
      />
      <FormTextField
        label="תיאור המשימה למתנדבים"
        InputProps={{ className: "description-input" }}
        inputProps={{ className: "description-textarea" }}
        withInfo="תיאור מפורט של פרטי המשימה, אפשרות להוסיף פרטים על המשימה שאינם נכללים בשדות הבאים"
        infoTimeout={7000}
        name="description"
        showErrorText={false}
        multiline
        minRows={1}
        maxRows={6}
        limit={VOLUNTEER_DESCRIPTION_MAX_LENGTH}
      />
      {requestDetails?.isOpportunityGroupParent ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {ADMIN_GROUP_LOCATION_PER_INSTANCE}
        </Typography>
      ) : (
      <Stack
        width="100%"
        direction="row"
        gap="4%"
        alignItems="center"
        flexWrap="wrap"
      >
        <FormGoogleAutocomplete
          containerProps={{
            width:
              isAddDestination || requestDetails.destination ? "42%" : "48%",
            height: "3.8rem",
          }}
          isRequest
          label={isAddDestination ? "מוצא" : "כתובת"}
          placeValue={requestDetails.location || ""} // Ensure fallback to an empty string
          name="location"
          showErrorText={false}
          InputProps={{
            endAdornment: (
              <IconButton
                onClick={() => openMapPicker("location")}
                size="small"
              >
                <PlaceOutlinedIcon fontSize="small" />
              </IconButton>
            ),
          }}
          onLocationChange={(location) => {
            setRequestDetails((prev) => {
              return {
                ...prev,
                location: location.locationName,
                coordinates: location.latLng,
              };
            });
            setValues((values) => {
              values.location = location.locationName;
              values.coordinates = location.latLng;
            }); // Update form values
          }}
        />
        {(isAddDestination || requestDetails.destination) && (
          <Tooltip title="החלפת מיקום">
            <IconButton
              style={{
                cursor: "pointer",
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-end",
              }}
              onClick={() => {
                setRequestDetails((prev) => {
                  return {
                    ...prev,
                    destination: prev.location,
                    destinationCoordinates: prev.coordinates,
                    location: prev.destination || undefined,
                    coordinates: prev.destinationCoordinates,
                  };
                });
                setValues((values) => {
                  const tempLocation = values.location;
                  const tempCoordinates = values.coordinates;
                  values.location = values.destination || tempLocation;
                  values.coordinates = values.destinationCoordinates;
                  values.destination = tempLocation;
                  values.destinationCoordinates = tempCoordinates;
                }); // Swap form values
              }}
            >
              <SyncAltIcon style={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        {isAddDestination || requestDetails.destination ? (
          <FormGoogleAutocomplete
            containerProps={{ width: "42%", height: "3.8rem" }}
            label="יעד"
            placeValue={requestDetails.destination || ""} // Ensure fallback to an empty string
            name="destination"
            isRequest
            showErrorText={false}
            onLocationChange={(location) => {
              setRequestDetails((prev) => {
                return {
                  ...prev,
                  destination: location.locationName,
                  destinationCoordinates: location.latLng,
                };
              });
              setValues((values) => {
                values.destination = location.locationName;
                values.destinationCoordinates = location.latLng;
              }); // Update form values
            }}
            InputProps={{
              sx: { paddingRight: "0" },
              endAdornment: (
                <>
                  <IconButton
                    onClick={() => openMapPicker("destination")}
                    size="small"
                  >
                    <PlaceOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => {
                      setIsAddDestination(false);
                      setRequestDetails((prev) => {
                        return {
                          ...prev,
                          destination: null,
                          destinationCoordinates: { lat: 0, lng: 0 },
                        };
                      });
                      setValues((values) => {
                        values.destination = null;
                        values.destinationCoordinates = { lat: 0, lng: 0 };
                      }); // Update form values
                    }}
                  >
                    <HighlightOffIcon />
                  </IconButton>
                </>
              ),
            }}
          />
        ) : (
          !isAddDestination &&
          !requestDetails.destination && (
            <Stack width="48%" height="3.8rem">
              <Button
                variant="outlined"
                sx={{
                  gap: "0.4rem",
                  borderRadius: "0.6rem",
                  height: "2rem",
                  marginTop: "1.8rem",
                  alignItems: "center",
                  borderColor: "rgba(0, 0, 0, 0.23)",
                }}
                onClick={() => {
                  setIsAddDestination(true);
                }}
              >
                <AddCircleOutlineOutlinedIcon sx={{ fontSize: "1.15rem" }} />
                הוספת יעד
              </Button>
            </Stack>
          )
        )}
      </Stack>
      )}
      <Stack
        width="100%"
        direction="row"
        gap="4%"
        marginTop="1vh"
        alignItems="center"
      >
        <FormTextField
          showErrorText={false}
          counter
          min={1}
          max={1000}
          label={
            requestDetails?.isOpportunityGroupParent
              ? ADMIN_GROUP_DEFAULT_AMOUNT
              : "כמות משתתפים"
          }
          className="amount-input"
          name="amount"
          containerProps={{ sx: { flex: 1, minWidth: 0 } }}
        />
        {remult.isAllowed(Roles.Admins) && <ManagerSelect />}
      </Stack>
      <Stack
        width="100%"
        direction="row"
        gap="4%"
        marginTop="1vh"
        alignItems="center"
      >
        <FormTextField
          showErrorText={false}
          counter
          min={MATCH_WEIGHT_MIN}
          max={MATCH_WEIGHT_MAX}
          label="מכפיל מאצ'ים"
          name="defaultMatchWeight"
          containerProps={{ sx: { flex: 1, minWidth: 0 } }}
        />
        <FormTextField
          showErrorText={false}
          label="יעד מתנדבים יומי"
          name="dailyVolunteerTarget"
          placeholder="ריק = בלי יעד"
          containerProps={{ sx: { flex: 1, minWidth: 0 } }}
        />
      </Stack>
      <FormTextField
        InputProps={{ className: "description-input" }}
        inputProps={{ className: "description-textarea" }}
        multiline
        maxRows={3}
        label="הערות לשימוש פנימי"
        name="comments"
      />
      {requestDetails?.id ? (
        <CommunicationRecordManager
          opportunityId={requestDetails.id}
          type="opportunity"
        />
      ) : (
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          ניתן להוסיף תיעוד תקשורת לאחר שמירת המשימה
        </Typography>
      )}
      <MapPinDialog
        open={isMapOpen}
        title={mapTarget === "location" ? "בחירת מיקום" : "בחירת יעד"}
        initialValue={currentMapCoordinates}
        onClose={() => setIsMapOpen(false)}
        onSave={handleMapSave}
      />
      <AidRequestAiFormatDialog
        open={isAiFormatOpen}
        suggestion={aiSuggestion}
        onClose={() => setIsAiFormatOpen(false)}
        onRetry={startFormat}
      />
    </Stack>
  );
};
